import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUniquePersonIdCount } from './personIdUtils';

const chipBaseSx = {
  display: 'flex',
  height: '24px',
  padding: '8px 12px',
  alignItems: 'center',
  gap: '4px',
  borderRadius: '8px',
  border: '1px solid #959696',
  background: '#FFF',
  whiteSpace: 'nowrap',
};

const chipLabelSx = {
  color: '#000',
  fontFamily: '"Noto Sans"',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '140%',
};

const hasPersonIdValues = (value) => {
  if (Array.isArray(value)) {
    return value.some((entry) => String(entry ?? '').trim());
  }

  if (typeof value === 'string') {
    return /[^\s,;\n\r]/.test(value);
  }

  return false;
};

const getDateBasisNotAppliedLabel = (value) => {
  const labels = {
    paid: 'Date Basis: Paid',
    incurred: 'Date Basis: Incurred'
  };

  return labels[String(value ?? '').trim().toLowerCase()] || null;
};

const isDateBasisFilter = (filterName) => filterName.startsWith('Date Basis:');

const getNotAppliedDisplayLabel = (filterName, appliedFilters) => {
  if (filterName === 'Person ID') {
    const count = getUniquePersonIdCount(appliedFilters.personIdValue);
    return count > 0 ? `Person ID: ${count} IDs` : 'Person ID';
  }
  return filterName;
};

const shouldShowNotAppliedFilter = (filterName, appliedFilters) => {
  if (filterName === 'Person ID') {
    return hasPersonIdValues(appliedFilters.personIdValue);
  }

  if (isDateBasisFilter(filterName)) {
    return filterName === getDateBasisNotAppliedLabel(appliedFilters.dateBasis);
  }

  return true;
};

const filterListsMatch = (first, second) => (
  first.length === second.length && first.every((filterName, index) => filterName === second[index])
);

const FiltersNotAppliedBanner = () => {
  const notAppliedFilters = useSelector((state) => state.globalFilters?.notAppliedFilters) || [];
  const appliedFilters = useSelector((state) => state.globalFilters?.applied) || {};
  const tableauAppliedFilterValues = useSelector((state) => state.globalFilters?.tableauAppliedFilterValues) || {};
  const currentTenantCd = useSelector((state) => state.user?.currentTenantCd);
  const companies = useSelector((state) => state.user?.companies);
  const [dismissed, setDismissed] = useState(false);
  const prevFiltersRef = useRef(notAppliedFilters);
  const prevVisibleFiltersRef = useRef([]);
  const currentTenant = Array.isArray(companies)
    ? companies.find((company) => company.code === currentTenantCd) || null
    : null;
  const areTenantFiltersEnabled = currentTenant?.filtersEnabled !== false;
  const visibleNotAppliedFilters = useMemo(
    () => notAppliedFilters
      .filter((filterName) => shouldShowNotAppliedFilter(filterName, appliedFilters)),
    [appliedFilters, notAppliedFilters],
  );

  // Reset dismissed when the underlying or currently visible filter list changes.
  useEffect(() => {
    const filtersChanged = !filterListsMatch(prevFiltersRef.current, notAppliedFilters);
    const visibleFiltersChanged = !filterListsMatch(prevVisibleFiltersRef.current, visibleNotAppliedFilters);

    if (filtersChanged || visibleFiltersChanged) {
      setDismissed(false);
      prevFiltersRef.current = notAppliedFilters;
      prevVisibleFiltersRef.current = visibleNotAppliedFilters;
    }
  }, [notAppliedFilters, visibleNotAppliedFilters]);

  if (dismissed || !areTenantFiltersEnabled || visibleNotAppliedFilters.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        padding: '16px 80px',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid #959696',
        borderBottom: '1px solid #959696',
        background: '#FAFFDB',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
        <Typography
          sx={{
            display: 'flex',
            minWidth: '120px',
            height: '24px',
            flexDirection: 'column',
            justifyContent: 'center',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            color: '#000',
            fontFamily: '"Noto Sans"',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '140%',
          }}
        >
          Filters Not Applied:
        </Typography>
        {visibleNotAppliedFilters.map((filterName) => {
          const label = getNotAppliedDisplayLabel(filterName, appliedFilters);
          const tooltipValues = filterName === 'Person ID' && tableauAppliedFilterValues.personId?.length
            ? tableauAppliedFilterValues.personId.join(', ')
            : '';

          const chip = (
            <Box key={filterName} sx={chipBaseSx}>
              <Typography component="span" sx={chipLabelSx}>
                {label}
              </Typography>
            </Box>
          );

          return tooltipValues ? (
            <Tooltip key={filterName} title={`Applied Person IDs: ${tooltipValues}`} arrow>
              {chip}
            </Tooltip>
          ) : chip;
        })}
      </Box>
      <IconButton
        aria-label="Dismiss filters not applied banner"
        onClick={() => setDismissed(true)}
        size="small"
        sx={{ flexShrink: 0, ml: 1 }}
      >
        <IconX size={14} stroke={2} color="black" />
      </IconButton>
    </Box>
  );
};

export default FiltersNotAppliedBanner;
